import { useRef } from 'react';
import { Stack, Group, Text, Badge, Anchor, Loader, Center } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { MediaStateType, MediaStatusType } from '@lib2/model/media/MediaTypes';
import { MediaType } from '@lib2/model/media/MediaType';
import { formatSmartDate, formatTimestamp } from '@/shared/utils/date';
import { MEDIA_STATUS_COLORS, MEDIA_STATUS_LABELS } from '@/lib/constants/media';
import { PLAYLIST_VIRTUALIZER_OVERSCAN } from '@/lib/constants/playlist';
import { PlaylistUtils } from '../PlaylistUtils';
import type { PlaylistMediaView, TagMatchPreview } from '@/lib/types/Playlist';

// Text-only rows keep the virtualizer measurement-free (same shape as the
// manual playlist items list).
const PREVIEW_ROW_HEIGHT = 44;
// Own scroll region: an unlimited smart playlist can match a whole library.
const PREVIEW_MAX_HEIGHT = '50vh';

interface SmartPlaylistPreviewProps {
  mediaType: MediaType;
  /** null while the media library is still loading. */
  preview: TagMatchPreview | null;
  /** Human label of the sort order in force ("Published Date (Newest First)"). */
  sortLabel: string;
}

/**
 * Read-only list of the media a smart playlist's CURRENT (possibly unsaved)
 * rules match, in playlist order and capped by its limit — the verification
 * step before Publish that the count line alone could not give. Evaluated
 * from the loaded library, so it is approximate in the same way the count is;
 * inactive and unprocessed items are listed with a badge because they match
 * the rules but do not play for viewers.
 */
export function SmartPlaylistPreview({ mediaType, preview, sortLabel }: SmartPlaylistPreviewProps) {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const noun = PlaylistUtils.getMediaNoun(mediaType);
  const detailBase = mediaType === MediaType.Video ? '/videos'
    : mediaType === MediaType.VideoStream ? '/streams'
    : null;

  const items = preview?.items ?? [];

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => PREVIEW_ROW_HEIGHT,
    overscan: PLAYLIST_VIRTUALIZER_OVERSCAN,
    getItemKey: (index) => items[index].media.mediaId,
  });

  // Legacy media kinds (audio etc.) have no library source in v3 — nothing to preview.
  if (detailBase == null) return null;

  const openDetail = (displayId: string) => {
    if (mediaType === MediaType.Video) {
      navigate({ to: '/videos/$id', params: { id: displayId } });
    } else {
      navigate({ to: '/streams/$id', params: { id: displayId } });
    }
  };

  const limitTruncates = preview != null && preview.limit != null && preview.matchedCount > preview.limit;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="baseline">
        <Group gap="xs" align="baseline">
          <Text fw={600} size="md">Matched {noun.pluralTitle}</Text>
          {preview && <Badge size="sm" variant="light">{items.length}</Badge>}
        </Group>
        {preview && items.length > 0 && (
          <Text size="xs" c="dimmed">Sorted by {sortLabel}</Text>
        )}
      </Group>
      <Text size="sm" c="dimmed">
        Read-only preview of what this playlist plays, in playlist order. It follows the rules
        above as you edit them; save to apply changes for viewers. Inactive {noun.plural} are
        listed for reference but don&rsquo;t play.
        {limitTruncates && (
          <> Limit {preview.limit}: the first {preview.limit} of ~{preview.matchedCount} matching {noun.plural}.</>
        )}
      </Text>

      {preview == null ? (
        <Center py="lg">
          <Loader size="sm" />
        </Center>
      ) : items.length === 0 ? (
        <Text c="dimmed" ta="center" py="lg">
          No {noun.plural} match these rules yet.
        </Text>
      ) : (
        <div
          ref={scrollContainerRef}
          style={{
            maxHeight: PREVIEW_MAX_HEIGHT,
            overflowY: 'auto',
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-md)',
            background: 'var(--mantine-color-white)',
          }}
        >
          <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((virtualItem) => (
              <PreviewRow
                key={virtualItem.key}
                item={items[virtualItem.index]}
                position={virtualItem.index + 1}
                top={virtualItem.start}
                href={`${detailBase}/${bcLong.getStr(items[virtualItem.index].media.mediaId)}`}
                onOpen={openDetail}
              />
            ))}
          </div>
        </div>
      )}
    </Stack>
  );
}

interface PreviewRowProps {
  item: PlaylistMediaView;
  position: number;
  top: number;
  href: string;
  onOpen: (displayId: string) => void;
}

function PreviewRow({ item, position, top, href, onOpen }: PreviewRowProps) {
  const { media } = item;
  const displayId = bcLong.getStr(media.mediaId);
  const isActive = media.mediaStateType === MediaStateType.Active;
  const isReady = media.mediaStatusType === MediaStatusType.Ready;
  // One badge, worst problem first: an unprocessed item never plays whatever its state.
  const badge = !isReady
    ? { label: MEDIA_STATUS_LABELS[media.mediaStatusType], color: MEDIA_STATUS_COLORS[media.mediaStatusType] }
    : !isActive
      ? { label: 'Inactive', color: 'gray' }
      : null;

  return (
    <Group
      gap="sm"
      wrap="nowrap"
      px="sm"
      style={{
        position: 'absolute',
        top,
        left: 0,
        width: '100%',
        height: PREVIEW_ROW_HEIGHT,
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        opacity: badge ? 0.7 : 1,
      }}
    >
      <Text size="xs" c="dimmed" w={36} ta="right">#{position}</Text>
      <Anchor
        href={href}
        size="sm"
        c="gray.8"
        fw={500}
        truncate="end"
        style={{ flex: 1, minWidth: 0 }}
        onClick={(e) => {
          // Plain left click navigates client-side; modified clicks keep native <a> behavior.
          if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            e.preventDefault();
            onOpen(displayId);
          }
        }}
      >
        {media.title}
      </Anchor>
      {badge && (
        <Badge size="xs" variant="light" color={badge.color}>{badge.label}</Badge>
      )}
      <Text
        size="xs"
        c="dimmed"
        w={90}
        ta="right"
        style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        title={media.published ? formatTimestamp(media.published) : undefined}
      >
        {media.published ? formatSmartDate(media.published) : '-'}
      </Text>
    </Group>
  );
}
